import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Birth-place autocomplete used across every tool page. Hits `/api/places/search`
 * with a debounce, shows suggestions, and on pick fires `onPick({ name, lat,
 * lon, timezone })` so the parent can set place + auto-fill timezone.
 *
 * Why a shared component: previously the KP/Planet-Effects/Bhrigu pages used
 * a plain <input> with no autocomplete, so users had to type the full place
 * name AND remember their timezone. This component is the same UX that
 * already works on Kundali, 20-Year, Compatibility and the BirthDetailsModal.
 *
 * Props
 *  - value       : current text in the field (controlled)
 *  - onChange    : (newText) => void — parent updates `place` state
 *  - onPick      : ({name, lat, lon, timezone}) => void — fires on suggestion select
 *  - inputClass  : optional className override for the <input> (defaults to
 *                  the warm-amber tool theme; KP page passes its dark theme)
 *  - dropdownClass : optional className override for the suggestion panel
 *  - placeholder : optional input placeholder
 *  - testId      : optional data-testid root for the input + suggestion buttons
 *  - required    : optional; default true
 *  - iconColor   : optional Phosphor `color` prop on the MapPin icon
 */
export default function PlaceAutocompleteInput({
  value,
  onChange,
  onPick,
  inputClass = "w-full px-3 py-2.5 pr-9 rounded-md border border-soft bg-bg text-sm outline-none focus:border-gold",
  dropdownClass = "absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg",
  placeholder = "Start typing a city — e.g. Mumbai, Delhi, London...",
  testId = "place",
  required = true,
  iconColor,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);

  // Close suggestions when clicking outside the wrapper.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleChange = (val) => {
    onChange?.(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = (val || "").trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      setShowSuggestions(trimmed.length > 0);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/places/search`, { params: { q: trimmed } });
        if (myReqId !== reqIdRef.current) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setShowSuggestions(true);
      } catch (_) {
        if (myReqId !== reqIdRef.current) return;
        setSuggestions([]);
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 250);
  };

  const pick = (s) => {
    onPick?.({ name: s.name, lat: s.lat, lon: s.lon, timezone: s.timezone });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          required={required}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setShowSuggestions(false); }}
          autoComplete="off"
          className={inputClass}
          data-testid={testId}
        />
        <MapPin
          size={14}
          color={iconColor}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          style={iconColor ? undefined : { color: "var(--muted, #9999BB)" }}
        />
      </div>
      {showSuggestions && (
        <div className={dropdownClass} data-testid={`${testId}-suggestions`}>
          {value && value.trim().length > 0 && value.trim().length < 2 && (
            <div className="px-3 py-2.5 text-xs opacity-60">Keep typing — at least 2 characters.</div>
          )}
          {searching && (
            <div className="px-3 py-2.5 text-xs opacity-70 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Searching for "{(value || "").trim()}"...
            </div>
          )}
          {!searching && value && value.trim().length >= 2 && suggestions.length === 0 && (
            <div className="px-3 py-2.5 text-xs opacity-60">
              No matches for "{value.trim()}". Try a fuller city name or include the country.
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.lat}-${s.lon}-${i}`}
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 hover:bg-[#D4AF37]/15 transition-colors border-b border-current/10 last:border-0"
              data-testid={`${testId}-option-${i}`}
            >
              <div className="text-sm">{s.name}</div>
              <div className="text-xs opacity-60 mt-0.5">{s.timezone}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
